/**
 * Leviathan Web - Customer Location Detail Page
 * Quasar Prime LLC
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import LocationMargins from './LocationMargins';
import {
  ArrowLeft,
  Edit,
  MapPin,
  Phone,
  Mail,
  User,
  Truck,
  Package,
  Ship,
  Building2,
  Calendar,
  Plus,
  DollarSign,
  Percent,
  FileText,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

interface Tank {
  id: string;
  name: string;
  tankNumber: string | null;
  capacity: number;
  currentLevel: number;
  product: {
    id: string;
    name: string;
    sku: string;
  } | null;
  status: string;
}

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  locationType: string | null;
  instructions: string | null;
  isActive: boolean;
  startDate: string;
  endDate: string | null;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
  tanks: Tank[];
  _count: {
    orders: number;
    shipments: number;
  };
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  PROJECT_SITE: 'Project Site',
  WAREHOUSE: 'Warehouse',
  FLEET_YARD: 'Fleet Yard',
  OFFICE: 'Office',
  OTHER: 'Other',
};

export default function CustomerLocationDetail() {
  const { customerId, id } = useParams();
  const { accessToken } = useAuth();
  const marginsRef = useRef<HTMLDivElement>(null);

  const [location, setLocation] = useState<CustomerLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scrollToMargins = () => {
    marginsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchLocation();
  }, [id]);

  const fetchLocation = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer-locations/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setLocation(data.location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch location');
    } finally {
      setIsLoading(false);
    }
  };

  const getLocationTypeLabel = (type: string | null) => {
    return type ? LOCATION_TYPE_LABELS[type] || type : 'Not specified';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTankFillPercent = (tank: Tank) => {
    if (!tank.capacity || tank.capacity === 0) return 0;
    return Math.round((tank.currentLevel / tank.capacity) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="text-center py-12">
        <MapPin size={48} className="mx-auto mb-4 text-text-tertiary" />
        <div className="text-text-tertiary font-mono mb-2">
          {error || 'Location not found'}
        </div>
        <Link
          to={`/customers/${customerId}/locations`}
          className="text-amber hover:text-amber/80 font-mono text-sm"
        >
          ← Back to locations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link
              to={`/customers/${customerId}/locations`}
              className="back-link mb-3"
            >
              <ArrowLeft size={14} />
              Back to Locations
            </Link>
            <div className="flex items-center gap-4">
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {location.name}
              </h1>
              <span
                className={`px-3 py-1 text-sm font-mono border ${
                  location.isActive
                    ? 'bg-success-green/20 text-success-green border-success-green'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500'
                }`}
              >
                {location.isActive ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
            <p className="text-text-secondary text-sm font-mono mt-1">
              {location.customer.name} • {location.customer.customerNumber}
            </p>
          </div>
        </div>

        <Link
          to={`/customers/${customerId}/locations/${id}/edit`}
          className="flex items-center gap-2 bg-steel hover:bg-steel/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm"
        >
          <Edit size={16} />
          Edit
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Truck} label="Tanks" value={location.tanks.length.toString()} />
        <StatCard icon={Package} label="Orders" value={location._count.orders.toString()} />
        <StatCard icon={Ship} label="Shipments" value={location._count.shipments.toString()} />
        <StatCard
          icon={Building2}
          label="Type"
          value={getLocationTypeLabel(location.locationType)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Address */}
          <Panel title="Address">
            <div className="flex items-start gap-3">
              <MapPin size={20} className="text-amber mt-1 flex-shrink-0" />
              <div className="space-y-1 font-mono text-text-primary">
                {location.address && <div>{location.address}</div>}
                {(location.city || location.state) && (
                  <div>
                    {location.city}
                    {location.city && location.state ? ', ' : ''}
                    {location.state} {location.zip}
                  </div>
                )}
                {!location.address && !location.city && (
                  <div className="text-text-tertiary text-sm italic">No address provided</div>
                )}
              </div>
            </div>
          </Panel>

          {/* Delivery Instructions */}
          {location.instructions && (
            <Panel title="Delivery Instructions">
              <div className="text-text-primary font-mono text-sm whitespace-pre-wrap bg-background/30 p-4 border-l-2 border-amber">
                {location.instructions}
              </div>
            </Panel>
          )}

          {/* Tanks at Location */}
          <Panel
            title={`Tanks at Location (${location.tanks.length})`}
            action={
              <Link
                to={`/tanks/new?customerId=${customerId}&locationId=${id}`}
                className="text-amber hover:text-amber/80 font-mono text-xs flex items-center gap-1"
              >
                <Plus size={14} />
                Add Tank
              </Link>
            }
          >
            {location.tanks.length === 0 ? (
              <div className="text-center py-8">
                <Truck size={32} className="mx-auto mb-3 text-text-tertiary" />
                <div className="text-text-tertiary font-mono text-sm mb-3">
                  No tanks at this location
                </div>
                <Link
                  to={`/tanks/new?customerId=${customerId}&locationId=${id}`}
                  className="text-amber hover:text-amber/80 font-mono text-sm"
                >
                  Add a tank →
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {location.tanks.map((tank) => {
                  const fillPercent = getTankFillPercent(tank);
                  const fillColor =
                    fillPercent < 25 ? 'bg-error-red' : fillPercent < 50 ? 'bg-amber' : 'bg-success-green';

                  return (
                    <Link
                      key={tank.id}
                      to={`/tanks/${tank.id}`}
                      className="block bg-background/50 border border-panel-border hover:border-amber/50 p-4 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-text-primary font-mono font-semibold">{tank.name}</div>
                        <div className="flex items-center gap-2">
                          {tank.tankNumber && (
                            <span className="text-text-tertiary font-mono text-xs">
                              #{tank.tankNumber}
                            </span>
                          )}
                          <span
                            className={`px-2 py-0.5 text-xs font-mono border ${
                              tank.status === 'ACTIVE'
                                ? 'bg-success-green/20 text-success-green border-success-green'
                                : 'bg-gray-500/20 text-gray-400 border-gray-500'
                            }`}
                          >
                            {tank.status}
                          </span>
                        </div>
                      </div>

                      {tank.product && (
                        <div className="text-text-secondary font-mono text-sm mb-3">
                          {tank.product.name}
                        </div>
                      )}

                      {/* Fill Level Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs font-mono text-text-tertiary">
                          <span>
                            {tank.currentLevel.toLocaleString()} / {tank.capacity.toLocaleString()}{' '}
                            gal
                          </span>
                          <span className={fillPercent < 25 ? 'text-error-red' : ''}>{fillPercent}%</span>
                        </div>
                        <div className="h-2 bg-background border border-panel-border">
                          <div
                            className={`h-full ${fillColor} transition-all`}
                            style={{ width: `${fillPercent}%` }}
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Panel>

          {/* Location Margins */}
          <div ref={marginsRef}>
            <Panel title="Product Margins">
              {customerId && id && (
                <LocationMargins customerId={customerId} locationId={id} />
              )}
            </Panel>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Contact */}
          <Panel title="Contact at Location">
            {fullName({ firstName: location.contactFirstName, lastName: location.contactLastName }) ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User size={16} className="text-text-tertiary" />
                  <span className="font-mono text-text-primary">{fullName({ firstName: location.contactFirstName, lastName: location.contactLastName })}</span>
                </div>
                {location.contactPhone && (
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-text-tertiary" />
                    <a
                      href={`tel:${location.contactPhone}`}
                      className="font-mono text-amber hover:text-amber/80"
                    >
                      {location.contactPhone}
                    </a>
                  </div>
                )}
                {location.contactEmail && (
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-text-tertiary" />
                    <a
                      href={`mailto:${location.contactEmail}`}
                      className="font-mono text-amber hover:text-amber/80 text-sm break-all"
                    >
                      {location.contactEmail}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-text-tertiary font-mono text-sm">No contact information</div>
            )}
          </Panel>

          {/* Details */}
          <Panel title="Details">
            <div className="space-y-4">
              {location.locationCode && (
                <InfoRow label="Location Code" value={location.locationCode} />
              )}
              <InfoRow label="Type" value={getLocationTypeLabel(location.locationType)} />
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-text-tertiary" />
                <div>
                  <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
                    Active Since
                  </div>
                  <div className="text-text-primary font-mono">{formatDate(location.startDate)}</div>
                </div>
              </div>
              {location.endDate && (
                <div>
                  <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-1">
                    Ended
                  </div>
                  <div className="text-text-primary font-mono">{formatDate(location.endDate)}</div>
                </div>
              )}
            </div>
          </Panel>

          {/* Quick Actions */}
          <Panel title="Quick Actions">
            <div className="space-y-2">
              <Link
                to={`/orders/new?customerId=${customerId}&locationId=${id}`}
                className="flex items-center justify-between w-full bg-amber hover:bg-amber/90 text-background font-mono py-3 px-4 text-sm transition-colors group"
              >
                <span className="flex items-center gap-2">
                  <Package size={16} />
                  <span>New Order</span>
                </span>
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to={`/tanks/new?customerId=${customerId}&locationId=${id}`}
                className="flex items-center justify-between w-full bg-background/50 border border-panel-border hover:border-amber/50 text-text-secondary hover:text-amber font-mono py-3 px-4 text-sm transition-colors group"
              >
                <span className="flex items-center gap-2">
                  <Truck size={16} />
                  <span>Add Tank</span>
                </span>
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Panel>

          {/* Location Settings */}
          <Panel title="Location Settings">
            <div className="space-y-2">
              <button
                onClick={scrollToMargins}
                className="flex items-center justify-between w-full bg-background/50 border border-panel-border hover:border-amber/50 text-text-secondary hover:text-text-primary font-mono py-3 px-4 text-sm transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <Percent size={16} className="text-amber" />
                  <div className="text-left">
                    <div className="font-semibold">Product Margins</div>
                    <div className="text-xs text-text-tertiary">Set location pricing</div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-text-tertiary group-hover:text-amber group-hover:translate-x-1 transition-all" />
              </button>
              <Link
                to={`/pricing/contracts/new?customerId=${customerId}&locationId=${id}`}
                className="flex items-center justify-between w-full bg-background/50 border border-panel-border hover:border-amber/50 text-text-secondary hover:text-text-primary font-mono py-3 px-4 text-sm transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <FileText size={16} className="text-steel" />
                  <div className="text-left">
                    <div className="font-semibold">Create Contract</div>
                    <div className="text-xs text-text-tertiary">Fixed price agreement</div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-text-tertiary group-hover:text-amber group-hover:translate-x-1 transition-all" />
              </Link>
              <Link
                to={`/customers/${customerId}`}
                className="flex items-center justify-between w-full bg-background/50 border border-panel-border hover:border-amber/50 text-text-secondary hover:text-text-primary font-mono py-3 px-4 text-sm transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <DollarSign size={16} className="text-success-green" />
                  <div className="text-left">
                    <div className="font-semibold">Customer Account</div>
                    <div className="text-xs text-text-tertiary">View credit & billing</div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-text-tertiary group-hover:text-amber group-hover:translate-x-1 transition-all" />
              </Link>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="relative bg-panel-dark border border-panel-border p-4">
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center gap-3">
        <Icon size={20} className="text-amber" />
        <div>
          <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
            {label}
          </div>
          <div className="text-xl font-display font-bold text-text-primary">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="relative">
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-amber">
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-1">
        {label}
      </div>
      <div className="text-text-primary font-mono">{value}</div>
    </div>
  );
}
